import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { apiService } from '../services/api';
import { UserResponse, BodyFatMeasurementResponse } from '../types/api';
import { toGreekUpperCase } from '../utils/greekUtils';
import SubscriptionHistoryModal from './SubscriptionHistoryModal';
import './ClientProfile.css';
import './Modal.css';

const ClientProfile: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user } = useAuth();
  const { t, formatDate: formatDateWithLocale } = useTranslation();
  const [profile, setProfile] = useState<UserResponse | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Body fat measurement state
  const [latestMeasurement, setLatestMeasurement] = useState<BodyFatMeasurementResponse | null>(null);
  const [allMeasurements, setAllMeasurements] = useState<BodyFatMeasurementResponse[]>([]);
  const [showMeasurementHistory, setShowMeasurementHistory] = useState(false);
  const [measurementHistoryLoading, setMeasurementHistoryLoading] = useState(false);

  // Ref to prevent duplicate API calls
  const fetchingRef = useRef(false);

  useEffect(() => {
    fetchProfileData();
  }, []);

  useEffect(() => {
    // Listen for appointment booking events to refresh data
    const handleAppointmentBooked = () => {
      fetchProfileData();
    };

    window.addEventListener('appointmentBooked', handleAppointmentBooked);
    
    return () => {
      window.removeEventListener('appointmentBooked', handleAppointmentBooked);
    };
  }, []);

  const fetchProfileData = useCallback(async () => {
    // Prevent duplicate calls
    if (fetchingRef.current) {
      return;
    }
    
    try {
      fetchingRef.current = true;
      setIsLoading(true);
      setError('');
      
      // Fetch profile
      const profileResponse = await apiService.getProfile();
      if (profileResponse.success && profileResponse.data) {
        setProfile(profileResponse.data);
      }

      // Fetch latest body fat measurement
      try {
        const measurementResponse = await apiService.getMyLatestBodyFatMeasurement();
        if (measurementResponse.success && measurementResponse.data) {
          setLatestMeasurement(measurementResponse.data);
        }
      } catch (measurementErr: any) {
        // 404 just means no measurements - that's fine
        if (measurementErr?.response?.status !== 404) {
          console.error('Error fetching latest measurement:', measurementErr);
        }
      }

    } catch (err: any) {
      console.error('Error fetching profile data:', err);
      setError(t('failedToLoadProfile'));
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [t]);



  const formatDate = (dateString: string) => {
    return formatDateWithLocale(dateString);
  };

  const formatDateDDMMYYYY = (dateStr: string): string => {
    if (!dateStr) return '';
    const datePart = dateStr.split('T')[0];
    const parts = datePart.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  const handleToggleMeasurementHistory = async () => {
    if (showMeasurementHistory) {
      setShowMeasurementHistory(false);
      return;
    }
    setMeasurementHistoryLoading(true);
    try {
      const response = await apiService.getMyBodyFatMeasurements();
      if (response.success && response.data) {
        setAllMeasurements(response.data);
      }
    } catch (err: any) {
      if (err?.response?.status !== 404) {
        console.error('Error fetching measurement history:', err);
      }
      setAllMeasurements([]);
    } finally {
      setMeasurementHistoryLoading(false);
      setShowMeasurementHistory(true);
    }
  };



  const getSubscriptionStatus = () => {
    if (!profile?.subscription) return toGreekUpperCase(t('noSubscription'));
    
    const now = new Date();
    // Use actualEndDate if available, otherwise fall back to endDate
    const endDateStr = profile.subscription.actualEndDate || profile.subscription.endDate;
    const endDate = new Date(endDateStr);
    
    if (endDate < now) return toGreekUpperCase(t('expired'));
    if (!profile.subscription.isActive) return toGreekUpperCase(t('inactive'));
    return toGreekUpperCase(t('active'));
  };

  const getSubscriptionStatusColor = (status: string) => {
    switch (status) {
      case toGreekUpperCase(t('active')):
        return '#22c55e';
      case toGreekUpperCase(t('expired')):
        return '#ef4444';
      case toGreekUpperCase(t('inactive')):
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const getAppointmentsRemaining = () => {
    if (!profile?.subscription) return null;
    
    const remaining = profile.subscription.monthlyLimit - profile.subscription.validAppointmentCount;
    return Math.max(0, remaining); // Ensure it doesn't go negative
  };



  if (isLoading) {
    return (
      <div className="client-profile">
        <div className="loading">{t('loadingProfile')}</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="client-profile">
        <div className="error-message">{t('failedToLoadProfile')}</div>
      </div>
    );
  }

  const subscriptionStatus = getSubscriptionStatus();
  const appointmentsRemaining = getAppointmentsRemaining();

  return (
    <div className="client-profile">
      <div className="profile-header">
        <h2>{t('myProfile')}</h2>
        <p>{t('viewYourAccountInformation')}</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="profile-grid">
        {/* Left column: Personal Information + Body Fat Measurements */}
        <div className="profile-column">
          {/* Personal Information */}
          <div className="profile-card">
            <div className="card-header">
              <h3>{t('personalInformation')}</h3>
              <span className="readonly-badge">{t('readOnly')}</span>
            </div>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('fullName')}</label>
                <span>{profile.name}</span>
              </div>
              <div className="info-item">
                <label>{t('email')}</label>
                <span>{profile.email}</span>
              </div>
              <div className="info-item">
                <label>{t('phoneNumber')}</label>
                <span>{profile.phone}</span>
              </div>
              <div className="info-item">
                <label>{t('memberSince')}</label>
                <span>{formatDate(profile.createdAt)}</span>
              </div>
            </div>
            <div className="card-note">
              <p>💡 {t('toUpdatePersonalInfo')}</p>
            </div>
          </div>

          {/* Body Fat Measurements */}
          <div className="profile-card">
            <div className="card-header">
              <h3>{t('measurementsSection')}</h3>
              {latestMeasurement && (
                <button
                  type="button"
                  onClick={handleToggleMeasurementHistory}
                  className="btn-secondary"
                  style={{ fontSize: '13px', padding: '6px 12px' }}
                >
                  {t('viewHistory')}
                </button>
              )}
            </div>

            {latestMeasurement ? (
              <div className="info-grid">
                <div className="info-item">
                  <label>{t('measurementDate')}</label>
                  <span>{formatDateDDMMYYYY(latestMeasurement.measurementDate)}</span>
                </div>
                <div className="info-item">
                  <label>{t('lastMeasurementDate')}</label>
                  <span>
                    {latestMeasurement.lastMeasurementDate
                      ? formatDateDDMMYYYY(latestMeasurement.lastMeasurementDate)
                      : t('noLastMeasurement')}
                  </span>
                </div>
              </div>
            ) : (
              <div style={{ padding: '16px 0', color: '#6b7280', textAlign: 'center' }}>
                {t('noMeasurements')}
              </div>
            )}
          </div>
        </div>

        {/* Subscription Information */}
        <div className="profile-card">
          <div className="card-header">
            <h3>{t('subscription')}</h3>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {profile.subscription && (
                <button
                  type="button"
                  onClick={() => setShowHistoryModal(true)}
                  className="btn-secondary"
                  style={{ fontSize: '13px', padding: '6px 12px' }}
                >
                  {t('viewHistory')}
                </button>
              )}
              <span
                className="status-badge"
                style={{ backgroundColor: getSubscriptionStatusColor(subscriptionStatus) }}
              >
                {subscriptionStatus}
              </span>
            </div>
          </div>
          
          {profile.subscription ? (
            <div className="subscription-details">
              <div className="subscription-section">
                <h4>{t('eligibilityWindow')}</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>{t('startDate')}</label>
                    <span>{formatDate(profile.subscription.startDate)}</span>
                  </div>
                  <div className="info-item">
                    <label>{t('endDate')}</label>
                    <span>{formatDate(profile.subscription.endDate)}</span>
                  </div>
                </div>
              </div>

              <div className="subscription-section">
                <h4>{t('activationStatus')}</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>{t('status')}</label>
                    <span className={`status-badge ${
                      profile.subscription.isFrozen ? 'frozen' : 
                      profile.subscription.isActivated ? 'activated' : 'waiting'
                    }`}>
                      {profile.subscription.isFrozen ? toGreekUpperCase(t('frozen')) : 
                       profile.subscription.isActivated ? toGreekUpperCase(t('active')) : toGreekUpperCase(t('waitingForFirstAppointment'))}
                    </span>
                  </div>
                  {profile.subscription.actualStartDate && (
                    <div className="info-item">
                      <label>{t('actualStartDate')}</label>
                      <span>{formatDate(profile.subscription.actualStartDate)}</span>
                    </div>
                  )}
                  {profile.subscription.actualEndDate && (
                    <div className="info-item">
                      <label>{t('actualEndDate')}</label>
                      <span>{formatDate(profile.subscription.actualEndDate)}</span>
                    </div>
                  )}
                  {profile.subscription.isFrozen && profile.subscription.frozenAt && (
                    <div className="info-item">
                      <label>{t('frozenAt')}</label>
                      <span>{formatDate(profile.subscription.frozenAt)}</span>
                    </div>
                  )}
                  {profile.subscription.remainingDaysAtFreeze !== undefined && profile.subscription.remainingDaysAtFreeze !== null && (
                    <div className="info-item">
                      <label>{t('remainingDaysAtFreeze')}</label>
                      <span>{profile.subscription.remainingDaysAtFreeze} {t('days')}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="subscription-section">
                <h4>{t('subscriptionDetails')}</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>{t('monthlyLimit')}</label>
                    <span>{profile.subscription.monthlyLimit} {t('sessions')}</span>
                  </div>
                  <div className="info-item">
                    <label>{t('validAppointments')}</label>
                    <span>{profile.subscription.validAppointmentCount} {t('sessions')}</span>
                  </div>
                  {appointmentsRemaining !== null && (
                    <div className="info-item">
                      <label>{t('appointmentsRemaining')}</label>
                      <span>{appointmentsRemaining} {t('appointments')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="no-subscription">
              <p>{t('noActiveSubscription')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Measurement History Modal */}
      {showMeasurementHistory && (
        <div className="modal-overlay" onClick={() => setShowMeasurementHistory(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2>{t('measurementHistory')}</h2>
              <button className="modal-close" onClick={() => setShowMeasurementHistory(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              {measurementHistoryLoading ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>{t('loading')}</div>
              ) : allMeasurements.length > 0 ? (
                <table style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  fontSize: '14px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{
                        textAlign: 'center',
                        padding: '12px 16px',
                        color: '#4a5568',
                        fontWeight: 600,
                        borderBottom: '2px solid #dc2626',
                        width: '60px'
                      }}>
                        #
                      </th>
                      <th style={{
                        textAlign: 'center',
                        padding: '12px 16px',
                        color: '#4a5568',
                        fontWeight: 600,
                        borderBottom: '2px solid #dc2626'
                      }}>
                        {t('measurementDate')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allMeasurements.map((m, index) => (
                      <tr
                        key={m.id}
                        style={{
                          borderBottom: index < allMeasurements.length - 1 ? '1px solid #e2e8f0' : 'none',
                          background: index % 2 === 0 ? '#ffffff' : '#f8fafc'
                        }}
                      >
                        <td style={{ padding: '11px 16px', textAlign: 'center', color: '#6b7280', fontWeight: 500 }}>
                          {index + 1}
                        </td>
                        <td style={{ padding: '11px 16px', textAlign: 'center', fontWeight: 500, color: '#1a202c' }}>
                          {formatDateDDMMYYYY(m.measurementDate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>
                  {t('noMeasurements')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Subscription History Modal */}
      <SubscriptionHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
      />
    </div>
  );
};

export default ClientProfile; 